import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { createBrowserRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import Page from './Page';
import { ConfigProvider, useConfig } from './providers/config/ConfigProvider.jsx';
import { TokenProvider } from './providers/token/TokenProvider.jsx';
import { Auth0Provider } from '@auth0/auth0-react';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import LoadingFallback from './components/LoadingFallback.jsx';

// Lazy load route components
const MarketingPage = lazy(() => import('./Marketing/Page').then((module) => ({ default: module.MarketingPage })));
const DashboardPage = lazy(() => import('./Dashboard/Page').then((module) => ({ default: module.DashboardPage })));
const ResumePage = lazy(() => import('./Resume/Page').then((module) => ({ default: module.ResumePage })));
const SpringAIPage = lazy(() => import('./Spring-AI/Page').then((module) => ({ default: module.SpringAIPage })));
const DocumentsPage = lazy(() => import('./Documents/Page').then((module) => ({ default: module.DocumentsPage })));
const AdminDashboard = lazy(() => import('./Admin/AdminDashboard.jsx'));
const BlogPage = lazy(() => import('./Blog/Page').then((module) => ({ default: module.BlogPage })));
const BlogPostPage = lazy(() => import('./Blog/BlogPostPage'));
const PricingPage = lazy(() => import('./components/Subscription/PricingPage'));

const root = ReactDOM.createRoot(document.getElementById('root'));
const router = createBrowserRouter([
  {
    path: '/',
    element: <App/>,
    children: [
      {
        path: '/',
        element: <Page/>,
        children: [
          {
            path: '/',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <MarketingPage/>
              </Suspense>
            ),
          },
          {
            path: '/dashboard',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ProtectedRoute>
                  <DashboardPage/>
                </ProtectedRoute>
              </Suspense>
            ),
          },
          {
            path: '/resume',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ProtectedRoute>
                  <ResumePage/>
                </ProtectedRoute>
              </Suspense>
            ),
          },
          {
            path: '/spring-ai',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ProtectedRoute>
                  <SpringAIPage/>
                </ProtectedRoute>
              </Suspense>
            ),
          },
          {
            path: '/documents',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ProtectedRoute>
                  <DocumentsPage/>
                </ProtectedRoute>
              </Suspense>
            ),
          },
          {
            path: '/blog',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <BlogPage/>
              </Suspense>
            ),
          },
          {
            path: '/blog/:slug',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <BlogPostPage/>
              </Suspense>
            ),
          },
          {
            path: '/pricing',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <PricingPage/>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: '/admin',
        element: (
          <Suspense fallback={<div>Loading...</div>}>
            <ProtectedRoute requireAdmin={true}>
              <AdminDashboard />
            </ProtectedRoute>
          </Suspense>
        ),
      },
    ],
  },
]);

function AuthWrapper() {
  const config = useConfig();

  if (!config) {
    return <div>Loading...</div>;
  }

  const onRedirectCallback = (appState) => {
    // Auth0 will call this after successful login
    console.log('Auth0 redirect callback, appState:', appState);
  };

  return (
    <Auth0Provider
      domain={config.auth.domain}
      clientId={config.auth.clientId}
      useRefreshTokens={true}
      cacheLocation={'localstorage'}
      onRedirectCallback={onRedirectCallback}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: config.auth.audience,
        scope: config.auth.scope,
      }}
    >
      <TokenProvider>
        <RouterProvider router={router}/>
      </TokenProvider>
    </Auth0Provider>
  );
}

root.render(
  <React.StrictMode>
    <ConfigProvider>
      <AuthWrapper/>
    </ConfigProvider>
  </React.StrictMode>,
);
